<template>
  <li
    class="side-nav-item"
    :class="{
      'mm-active': isActive,
      'mm-child-active': isActive && props.isChild
    }"
  >
    <template v-if="item.child">
      <a
        data-bs-toggle="collapse"
        :href="'#sidebarDashboards' + item.name"
        :aria-expanded="isActive"
        :aria-controls="'sidebarDashboards' + item.name"
        class="side-nav-link hover-side-nav-link-bg-with-child"
        @click.stop="toggleItem"
        @dblclick.stop.prevent=""
      >
        <div class="link-bg-with-child-wrapper">
          <i :class="item.icon" />
          <span v-if="item.child" class="menu-arrow" @click.stop="arrowToggleItem" @dblclick.stop.prevent=""></span>
          <span class="pointer-none">{{ item.text }}</span>
        </div>
      </a>
      <div class="collapse" :id="'sidebarDashboards' + item.name" :class="{ show: isActive }">
        <List
          :listItems="item.child"
          itemClass="side-nav-second-level mm-collapse pt-0"
          :class="{ 'mm-show': isActive }"
          :isChild="true"
        />
      </div>
    </template>
    <template v-else>
      <router-link
        :to="{ name: item.name }"
        class="side-nav-link d-flex text-decoration-none justify-content-between"
        @click.stop="handleEvent"
      >
        <div class="hover-side-nav-link-bg d-flex align-items-center">
          <div class="icon-container">
            <i :class="icon"></i>
            <span class="noti-icon-badge" v-show="isBadgeCountVisible === true"></span>
          </div>
          <span>
            {{ item.text }}
          </span>
        </div>
        <div v-if="isBadgeVisible === true || isBadgeCountVisible === true" class="d-flex align-items-center">
          <div class="d-flex">
            <span v-if="isBadgeVisible === true" class="badge p-1 text-white ms-1 flex" :class="item.badge.class">
              {{ item.badge.text }}
            </span>
            <span v-if="isBadgeCountVisible === true" class="badge num-badge ms-1 flex">
              {{ item.badgeCount }}
            </span>
          </div>
        </div>
      </router-link>
    </template>
  </li>
</template>

<script setup>
import { computed, ref } from 'vue'

import { useStore } from 'vuex'

import List from '@/components/List'

// #region - Props
const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  index: {
    type: String,
    required: false,
    default: null
  },
  isChild: {
    type: Boolean,
    required: false,
    default: false
  }
})
// endregion

// #region - references
const lastClickTime = ref(0)
const arrowLastClickTime = ref(0)
const childMenuClicked = ref(false)
// endregion

// #region - Vuex state
const store = useStore()
const sidebarActiveMenuIndex = computed(() => store.state.sideMenu.sidebarActiveMenuIndex)
const sidebarActive = computed(() => store.state.sideMenu.sidebarActive)
const isBadgeVisible = computed(() => props.item.badge && (props.item.badge.show?.() ?? true))
const isBadgeCountVisible = computed(() => props.item.badgeCount && props.item.badgeCount > 0)
const icon = computed(() => props.item.icon + (isBadgeCountVisible.value ? ' has-new-item' : ''))
// endregion

// #region - computed properties
const isActive = computed(() => hasActiveChild(props.item))

// endregion

// #region - Method definitions
const hasActiveChild = subItem => {
  if (subItem.name === sidebarActiveMenuIndex.value) {
    return true
  }

  if (subItem.child) {
    return subItem.child.some(childItem => hasActiveChild(childItem))
  }

  return false
}

const toggleItem = el => {
  store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', props.index)

  const currentTime = Date.now()
  const timeDifference = currentTime - lastClickTime.value

  // Detected a double-click, ignoring or handling it specifically.
  if (timeDifference < 500) {
    return
  }

  sidebarActive.value = false

  if (el.target.nextSibling) {
    el.target.nextSibling.classList.toggle('mm-show')
  }

  if (props.isChild === false) {
    if (sidebarActive.value === false) {
      localStorage.setItem('sidebarOpened', '1')
      document.body.className = 'sidebar-enable'
      document.body.setAttribute('data-leftbar-compact-mode', 'scrollable')
      store.commit('sideMenu/SET_SIDEBAR_COLLAPSE_STATE', true)
    }
  } else {
    handleEvent(el)
  }
  lastClickTime.value = currentTime
}

const arrowToggleItem = el => {
  store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', props.index)

  const currentTime = Date.now()
  const timeDifference = currentTime - arrowLastClickTime.value

  // Detected a double-click, ignoring or handling it specifically.
  if (timeDifference < 500) {
    return
  }

  if (el.target.parentElement.parentElement) {
    el.target.parentElement.parentElement.classList.toggle('mm-active')
  }

  if (el.target.parentElement.nextSibling) {
    el.target.parentElement.nextSibling.classList.toggle('mm-show')
  }

  if (props.isChild === false) {
    if (sidebarActive.value === false) {
      localStorage.setItem('sidebarOpened', '1')
      document.body.className = 'sidebar-enable'
      document.body.setAttribute('data-leftbar-compact-mode', 'scrollable')
      store.commit('sideMenu/SET_SIDEBAR_COLLAPSE_STATE', true)
    }
  }
  store.commit('sideMenu/SET_SIDEBAR_ACTIVE', !childMenuClicked.value)

  arrowLastClickTime.value = currentTime
}

const handleEvent = element => {
  store.commit('sideMenu/SET_ACTIVE_MENU_INDEX', props.index)
}
// endregion
</script>

<style lang="scss" scoped>
.num-badge {
  background-color: #ffeaed;
  color: #ea3e53;
}

.icon-container {
  position: relative;
  display: inline-block;
}

body[data-leftbar-compact-mode='condensed'] .icon-container .has-new-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 15px;
  width: 12px;
  height: 12px;
  background-color: #e94f61;
  border-radius: 50%;
  border: 2px solid white;
}
</style>
